import React, { useEffect, useState } from "react";
import axios from "axios";

import { Badge, Button, Card, Icon, IconButton, Input, StatePanel, Toggle } from "../../components/ui";
import { updateBetaStatus } from "./api.js";

/**
 * Beta-button reports on the Monitoring board: the detail panel a report row
 * opens (with the same Resolve / Ignore / Reopen as an error group), and the
 * master toggle that shows the button on every store.
 */

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
// Matches the board's State pills — never the Where column's red/grey.
const STATE_TONE = { open: "warning", resolved: "success", ignored: "info" };

// "Mobile · Safari (iOS)" from the user agent the server stored with the report.
// Order matters: Edge/Opera/Samsung UAs also say Chrome, and Chrome's says Safari.
const BROWSERS = [
  [/Edg(e|A|iOS)?\//, "Edge"],
  [/OPR\/|Opera/, "Opera"],
  [/SamsungBrowser/, "Samsung Internet"],
  [/Firefox\/|FxiOS/, "Firefox"],
  [/Chrome\/|CriOS/, "Chrome"],
  [/Safari\//, "Safari"],
];
const SYSTEMS = [
  [/iPhone|iPad|iPod/, "iOS"],
  [/Android/, "Android"],
  [/Windows/, "Windows"],
  [/Mac OS X|Macintosh/, "macOS"],
  [/Linux/, "Linux"],
];
export const deviceFromUserAgent = (ua) => {
  if (!ua) return "—";
  const pick = (list) => list.find(([pattern]) => pattern.test(ua))?.[1];
  const device = /Mobi|Android|iPhone|iPad|iPod/.test(ua) ? "Mobile" : "Desktop";
  const system = pick(SYSTEMS);
  return `${device} · ${pick(BROWSERS) || "Other"}${system ? ` (${system})` : ""}`;
};

/** "Show the beta button on all stores" — compact, for the page header. */
export const BetaToggle = () => {
  const [enabled, setEnabled] = useState(null); // null = not loaded

  useEffect(() => {
    let cancelled = false;
    axios.get("/api/beta-reporting/config", { metadata: { silent: true } })
      .then(({ data }) => !cancelled && setEnabled(Boolean(data?.enabled)))
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (enabled === null) return null;

  return (
    <Toggle
      checked={enabled}
      label="Beta button on all stores"
      // Returning the promise gives Toggle's optimistic update + rollback.
      onChange={(next) => axios.put("/api/beta-reporting/config", { enabled: next }).then(() => setEnabled(next))}
    />
  );
};

const BetaReportDetail = ({ id, onClose, onChanged }) => {
  const [state, setState] = useState({ loading: true });
  const [shotUrl, setShotUrl] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setState({ loading: true });
    setShotUrl(null);

    axios.get(`/api/beta-reports/${id}`, { metadata: { silent: true } })
      .then(({ data }) => {
        if (cancelled) return;
        setState({ data });
        setNote(data?.report?.resolution_note || "");
        if (data?.report?.has_screenshot) {
          axios.get(`/api/beta-reports/${id}/screenshot`, { responseType: "blob", metadata: { silent: true } })
            .then(({ data: blob }) => {
              if (cancelled) return;
              objectUrl = URL.createObjectURL(blob);
              setShotUrl(objectUrl);
            })
            .catch(() => {});
        }
      })
      .catch((e) => !cancelled && setState({ error: e.response?.data?.message || "Couldn't load this report." }));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  const report = state.data?.report;
  // Missing until the backend that adds it is live — treat as open.
  const reportState = report?.state || "open";

  const setStatus = async (next) => {
    setSaving(true);
    try {
      const { report: row } = await updateBetaStatus(id, { state: next, note: note || null });
      setState((s) => ({ ...s, data: { ...s.data, report: { ...s.data.report, state: row.state } } }));
      onChanged?.();
    } catch (_) {
      /* best-effort, like the error panel; the list refresh shows the truth */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card as="aside" surface="raised" padding="lg" className="mon-page__detail">
      <div className="mon-page__detail-head">
        <Badge tone="accent" size="sm">Beta report</Badge>
        {report && (
          <Badge tone={STATE_TONE[reportState] || "info"} size="sm" dot>
            {reportState.charAt(0).toUpperCase() + reportState.slice(1)}
          </Badge>
        )}
        <IconButton label="Close" variant="ghost" onClick={onClose}>
          <Icon name="X" size="md" />
        </IconButton>
      </div>

      {state.loading ? (
        <StatePanel state="loading" compact title="Loading…" description={null} />
      ) : state.error ? (
        <StatePanel state="error" compact title={state.error} description={null} />
      ) : (
        <>
          <h2 className="mon-page__detail-title">Report #{report.id}</h2>
          <div className="mon-page__detail-meta">
            <span>{when(report.client_time || report.created_at)}</span>
            <span>· {report.store?.name || "—"}</span>
            <span>· {report.user?.name || "—"}</span>
          </div>

          <div className="mon-page__kv">
            <span>Device</span>
            <code title={report.user_agent || ""}>{deviceFromUserAgent(report.user_agent)}</code>
          </div>
          {report.url && (
            <div className="mon-page__kv">
              <span>Page</span>
              <code>{report.url}</code>
            </div>
          )}
          {/* One transcript (Alex, 2026-10-14): Gemini's, which is the accurate
              one. The browser's live text is shown only when Gemini produced
              nothing, so a failed transcription never loses the note. */}
          {(report.transcript || report.live_transcript) && (
            <div className="mon-page__kv">
              <span>Transcript</span>
              <p className="mon-beta__note">{report.transcript || report.live_transcript}</p>
            </div>
          )}

          <div className="mon-page__actions">
            <Button variant="primary" size="sm" disabled={saving} onClick={() => setStatus("resolved")}>
              Mark resolved
            </Button>
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => setStatus("ignored")}>
              Ignore
            </Button>
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => setStatus("open")}>
              Reopen
            </Button>
          </div>
          <p className="mon-page__handover-hint">
            Resolving adds this to the Incident log automatically — Claude writes up the cause and the lesson overnight.
          </p>
          <div className="mon-page__resolve-inputs">
            <Input
              size="md"
              placeholder="Resolution note — optional"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {report.has_screenshot && (
            <>
              <h3 className="mon-page__events-head">Screenshot</h3>
              {shotUrl ? (
                <a href={shotUrl} target="_blank" rel="noopener noreferrer" className="mon-beta__shot">
                  <img src={shotUrl} alt={`Screenshot attached to report ${report.id}`} />
                </a>
              ) : (
                <StatePanel state="loading" compact title="Loading screenshot…" description={null} />
              )}
            </>
          )}

          <h3 className="mon-page__events-head">Logs &amp; server errors around it</h3>
          <pre className="mon-page__bundle">
            {JSON.stringify({ logs: report.logs, server_errors_around: state.data.server_errors_around }, null, 2)}
          </pre>
        </>
      )}
    </Card>
  );
};

export default BetaReportDetail;
